import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import styled from "@emotion/styled";
import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  BillingDetails,
  LineItem,
  PaymentMethod,
  ShippingAddress,
  ShippingRate,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from "@stripe/stripe-js";
import { useState } from "react";

// Add global declaration for the Google Pay sheet on window
declare global {
  interface Window {
    gpay?: {
      close: () => void;
    };
  }
}

// Helper to detect Safari browser
const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1;
};

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const StripeExpressCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
}: {
  onSuccess: (
    paymentMethod: PaymentMethod,
    shippingAddress?: ShippingAddress,
    billingDetails?: BillingDetails
  ) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    console.log("stripe express checkout onConfirm event", event);

    if (!stripe || !elements) {
      onError(new Error("An unexpected error occurred"));
      return;
    }

    const res = await elements.submit();

    const { error: submitError } = res;

    if (submitError) {
      onError(new Error(submitError?.message ?? "An error occurred"));
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
    });

    if (error?.message) {
      onError(new Error(error.message));
    } else if (paymentMethod) {
      // In Safari, add a slight delay before sending success to allow the sheet to close
      if (isSafari()) {
        console.log(
          "Delaying success event in Safari to allow Google Pay sheet to close"
        );
        // Send the success event after a delay
        setTimeout(() => {
          console.log("Now sending delayed PAYMENT_METHOD_SUCCESS in Safari");
          onSuccess(paymentMethod, event.shippingAddress, event.billingDetails);
        }, 1_000);
      } else {
        // For other browsers, send immediately
        onSuccess(paymentMethod, event.shippingAddress, event.billingDetails);
      }
    }
  };
  return (
    <Container className="checkout-express-payment-container">
      <ExpressCheckoutElement
        className="express-checkout-element"
        options={{
          business: {
            name: "Brandclub",
          },
          buttonType: {
            applePay: "buy",
            googlePay: "buy",
          },
          buttonTheme: {
            applePay: "white-outline",
            googlePay: "white",
          },
          emailRequired: true,
          phoneNumberRequired: true,
          shippingAddressRequired: true,
          billingAddressRequired: true,
          paymentMethods: {
            applePay: "always",
            googlePay: "always",
          },
          shippingRates,
          lineItems,
        }}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
        onConfirm={onConfirm}
      />
    </Container>
  );
};

// const StripeExpressCheckoutV2 = ({
//   onSuccess,
//   onError,
//   onShippingAddressChange,
//   onShippingRateChange,
//   shippingRates,
//   lineItems,
// }: {
//   onSuccess: (
//     paymentMethod: PaymentMethod,
//     shippingAddress?: ShippingAddress,
//     billingDetails?: BillingDetails
//   ) => void;
//   onError: (error: Error) => void;
//   onShippingAddressChange: (
//     event: StripeExpressCheckoutElementShippingAddressChangeEvent
//   ) => void;
//   onShippingRateChange: (
//     event: StripeExpressCheckoutElementShippingRateChangeEvent
//   ) => void;
//   shippingRates: ShippingRate[];
//   lineItems: LineItem[];
// }) => {
//   const stripe = useStripe();
//   const elements = useElements();

//   const onConfirmExpressCheckout = async (
//     event: StripeExpressCheckoutElementConfirmEvent
//   ) => {
//     console.log("onConfirmExpressCheckout event", event);

//     if (!stripe || !elements) {
//       onError(new Error("An unexpected error occurred"));
//       return;
//     }

//     const res = await elements.submit();

//     const { error: submitError } = res;

//     if (submitError) {
//       onError(new Error(submitError?.message ?? "An error occurred"));
//       return;
//     }

//     const { error, paymentMethod } = await stripe.createPaymentMethod({
//       elements,
//     });

//     if (error?.message) {
//       onError(new Error(error.message));
//     } else if (paymentMethod) {
//       // In Safari, add a slight delay before sending success to allow the sheet to close
//       if (isSafari()) {
//         console.log(
//           "Delaying success event in Safari to allow Google Pay sheet to close"
//         );
//         // Send the success event after a delay
//         setTimeout(() => {
//           console.log("Now sending delayed PAYMENT_METHOD_SUCCESS in Safari");
//           onSuccess(paymentMethod, event.shippingAddress, event.billingDetails);
//         }, 1_000);
//       } else {
//         // For other browsers, send immediately
//         onSuccess(paymentMethod, event.shippingAddress, event.billingDetails);
//       }
//     }
//   };
//   return (
//     <Container className="checkout-express-payment-container">
//       <ExpressCheckoutElement
//         className="express-checkout-element"
//         options={{
//           business: {
//             name: "Brandclub",
//           },
//           buttonType: {
//             applePay: "buy",
//             googlePay: "plain",
//           },
//           buttonTheme: {
//             applePay: "white-outline",
//             googlePay: "white",
//           },
//           emailRequired: true,
//           phoneNumberRequired: true,
//           shippingAddressRequired: true,
//           billingAddressRequired: true,
//           paymentMethods: {
//             applePay: "always",
//             googlePay: "always",
//           },
//           lineItems,
//           shippingRates,
//         }}
//         onShippingAddressChange={onShippingAddressChange}
//         onShippingRateChange={onShippingRateChange}
//         onConfirm={onConfirmExpressCheckout}
//       />
//     </Container>
//   );
// };

const StripeCardCheckout = ({
  onSuccess,
  onError,
  onCancel,
}: {
  onCancel: () => void;
  onSuccess: (
    paymentMethod: PaymentMethod,
    shippingAddress?: ShippingAddress,
    billingDetails?: BillingDetails
  ) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const user = useAppSelector((state) => state.userProfile);

  const [isProcessing, setIsProcessing] = useState(false);

  const handlePaymentSubmit = async () => {
    if (!stripe || !elements) {
      onError(new Error("An unexpected error occurred"));
      return;
    }

    elements.submit();

    setIsProcessing(true);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });

      if (error) {
        onError(new Error(error.message ?? "An error occurred"));
      }
      if (paymentMethod) {
        // In Safari, add a slight delay before sending success
        if (isSafari()) {
          // Send the success event after a delay
          setTimeout(() => {
            onSuccess(paymentMethod);
          }, 1_000);
        } else {
          // For other browsers, send immediately
          onSuccess(paymentMethod);
        }
      }
    } catch (e) {
      console.error(e);
      onError(new Error("An unexpected error occurred"));
    } finally {
      setIsProcessing(false);
    }
  };

  const [loaded, setLoaded] = useState(false);
  const [completed, setCompleted] = useState(false);

  if (!user) {
    return null;
  }

  return (
    <>
      <div>
        <PaymentElement
          onReady={() => {
            setLoaded(true);
          }}
          onChange={(event) => {
            setCompleted(event.complete);
          }}
          options={{
            fields: {
              billingDetails: {
                name: "auto",
                email: "auto",
                phone: "auto",
                address: {
                  country: "auto",
                  postalCode: "auto",
                  state: "auto",
                  city: "auto",
                  line1: "auto",
                  line2: "auto",
                },
              },
            },
            defaultValues: {
              billingDetails: {
                name: `${user.given_name} ${user.family_name}`,
                email: user.email,
                phone: user.phoneNumber,
              },
            },
          }}
        />

        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "22px",
          }}
        >
          <OutlineButton
            onClick={() => {
              setIsProcessing(true);
              onCancel();
              setIsProcessing(false);
            }}
            disabled={isProcessing || !loaded}
          >
            {isProcessing ? "Cancelling" : "Cancel"}
          </OutlineButton>
          <SolidButton
            onClick={handlePaymentSubmit}
            disabled={isProcessing || !loaded || !completed}
          >
            {isProcessing ? "Saving" : "Save"}
          </SolidButton>
        </div>
      </div>
    </>
  );
};

export const StripeCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
  onCancel,
}: {
  onCancel: () => void;
  onSuccess: (
    paymentMethod: PaymentMethod,
    shippingAddress?: ShippingAddress,
    billingDetails?: BillingDetails
  ) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
}) => {
  const user = useAppSelector((state) => state.userProfile);

  if (!user) {
    return null;
  }

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <StripeExpressCheckout
          onSuccess={onSuccess}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          lineItems={lineItems}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "20px 0",
          color: "#666",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
        <span
          style={{
            margin: "0 16px",
            fontSize: "14px",
          }}
        >
          Add new credit card
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
      </div>
      <div>
        <StripeCardCheckout
          onSuccess={onSuccess}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          onCancel={onCancel}
        />
      </div>
    </>
  );
};
